import { cx } from "@suse-o11y/class-variance";
import { isPresent } from "@suse-o11y/is-present";
import { useComputed } from "@suse-o11y/signal";
import type { SignalForm } from "@suse-o11y/signal/form";
import { Slot, type Slottable } from "@suse-o11y/slot";
import React, { useMemo } from "react";

import { EventGate } from "~/components/event-gate";

import styles from "./_form.module.css";
import { useFormFieldContext } from "./use-form-field-context";

export interface FormFieldResetProps extends Slottable, React.ComponentPropsWithoutRef<"button"> {
	/**
	 * The {@link SignalForm} state to reset.
	 * If not provided, it will use the state from the FormField context.
	 */
	state?: SignalForm | ReadonlyArray<SignalForm | undefined>;
}

export const FormFieldReset = React.forwardRef<HTMLButtonElement, FormFieldResetProps>(
	({ asChild, className, onClick, state, children, ...props }, ref) => {
		const Root = asChild ? Slot : "button";
		const context = useFormFieldContext();
		const formState = state ?? context?.state;

		if (formState == null) {
			throw new Error("FormFieldReset must be used within a FormField component or with a `state` prop.");
		}

		const filteredFormState = useMemo(() => [formState].flat().filter(isPresent), [formState]);

		const dirty = useComputed(
			(monitor) => filteredFormState.some((item) => item.isDirty(monitor)),
			[filteredFormState],
		);

		const submitting = useComputed(
			(monitor) => filteredFormState.some((item) => item.isSubmitting(monitor)),
			[filteredFormState],
		);

		return (
			// TODO OBSFE-128 utilize the ~/components/link component
			<EventGate<"button">
				aria-busy={submitting}
				aria-hidden={!dirty}
				className={cx(styles.formFieldReset, asChild && styles.unstyled, className)}
				listening={!submitting}
				onClick={(event) => {
					filteredFormState.forEach((item) => {
						item.reset();
					});
					onClick?.(event);
				}}
				ref={ref}
				tabIndex={dirty ? undefined : -1}
				{...props}
			>
				<Root type="button">{children ?? "Reset"}</Root>
			</EventGate>
		);
	},
);
